// src/components/AdminSidebar.jsx
import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import "./AdminSidebar.css";

const Icon = ({ name, size = 20, stroke = "currentColor" }) => {
  const P = {
    menu: "M3 6h18M3 12h18M3 18h18",
    home: "M3 11.5 12 4l9 7.5v7.5H15v-6H9v6H3z",
    chat: "M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z",
    check: "M20 6 9 17l-5-5",
    users:
      "M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2m14-11a4 4 0 1 0-8 0 4 4 0 0 0 8 0z",
    folder: "M3 7h6l2 2h10v10H3z",
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={P[name] || P.menu} />
    </svg>
  );
};

const ColorIcon = ({ icon, bg }) => (
  <span className="adm-color-icon" style={{ background: bg }}>
    {icon}
  </span>
);

const SubIcon = ({ icon, bg = "#f1f5f9" }) => (
  <span className="adm-sub-icon" style={{ background: bg }} aria-hidden>
    {icon}
  </span>
);

// Chevron: single reusable expand/collapse indicator.
const Chevron = ({ open, size = 16 }) => (
  <span
    aria-hidden="true"
    className={`adm-chevron${size === 14 ? " adm-chevron--small" : ""}`}
  >
    {open ? "▲" : "▼"}
  </span>
);

const AdminSidebar = ({
  unreadCount = 0,
  breakpoint = 992,
  hideFab = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const mqQuery = `(max-width: ${breakpoint - 0.02}px)`;

  const getIsMobile = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.(mqQuery).matches || window.innerWidth < breakpoint);

  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [drawerOpen, setDrawerOpen] = useState(false);

  const open = () => setDrawerOpen(true);
  const close = () => setDrawerOpen(false);
  const toggle = () => setDrawerOpen((value) => !value);

  useEffect(() => {
    const mediaQuery = window.matchMedia(mqQuery);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener?.("change", handleChange);

    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, [mqQuery]);

  useEffect(() => {
    window.adminSidebarOpen = open;
    window.adminSidebarClose = close;
    window.adminSidebarToggle = toggle;

    return () => {
      delete window.adminSidebarOpen;
      delete window.adminSidebarClose;
      delete window.adminSidebarToggle;
    };
  }, []);

  useEffect(() => {
    const handleOpen = () => open();
    const handleClose = () => close();
    const handleToggle = () => toggle();

    window.addEventListener("sidebar:open", handleOpen);
    window.addEventListener("sidebar:close", handleClose);
    window.addEventListener("sidebar:toggle", handleToggle);

    document.addEventListener("sidebar:open", handleOpen);
    document.addEventListener("sidebar:close", handleClose);
    document.addEventListener("sidebar:toggle", handleToggle);

    return () => {
      window.removeEventListener("sidebar:open", handleOpen);
      window.removeEventListener("sidebar:close", handleClose);
      window.removeEventListener("sidebar:toggle", handleToggle);

      document.removeEventListener("sidebar:open", handleOpen);
      document.removeEventListener("sidebar:close", handleClose);
      document.removeEventListener("sidebar:toggle", handleToggle);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
      if (event.key?.toLowerCase?.() === "m") toggle();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const routeOpenState = (pathname) => ({
    tools: pathname.startsWith("/tools"),

    messenger: pathname.startsWith("/messenger"),
    email: pathname.startsWith("/email"),
    hr:
    
      pathname.startsWith("/admin/log") ||
      pathname.startsWith("/employee-drawing-report") ||
      pathname.startsWith("/leave-application") ||
      pathname.startsWith("/leave-management") ||
      pathname.startsWith("/manage-employees") ||
      pathname.startsWith("/role-permissions") ||
      pathname.startsWith("/admin-employee-review-detail"),


    hiring:
      pathname.startsWith("/hr-dashboard") ||
      pathname.startsWith("/hr/job-openings") ||
      pathname.startsWith("/hr/candidates") ||
      pathname.startsWith("/hr/interviews") ||
      pathname.startsWith("/hr/offers") ||
      pathname.startsWith("/hr/joining") ||
      pathname.startsWith("/hr/closed-hiring") ||
      pathname.startsWith("/hr/recruitment-reports"),

    operations:
      pathname.startsWith("/transmittal") ||
      pathname.startsWith("/checklist") ||
      pathname.startsWith("/projects") ||
      pathname.startsWith("/drawing-master"),
    architect: pathname.startsWith("/construction-report"),

    // Project Management is its own top-level section (not nested under Operations)
    projectManagement:
      pathname.startsWith("/project-management") ||
      pathname.startsWith("/task-board") ||
      pathname.startsWith("/gantt-chart") ||
      pathname.startsWith("/calendar") ||
      pathname.startsWith("/drawings") ||
      pathname.startsWith("/boq-estimates") ||
      pathname.startsWith("/site-management") ||
      pathname.startsWith("/procurement") ||
      pathname.startsWith("/client-approval") ||
      pathname.startsWith("/reports"),

    finance:
      pathname.startsWith("/finance") ||
      pathname.startsWith("/sales") ||
      pathname.startsWith("/quotation") ||
      pathname.startsWith("/proforma-invoice") ||
      pathname.startsWith("/invoice") ||
      pathname.startsWith("/finance-entry") ||
      pathname.startsWith("/company-expenses") ||
      pathname.startsWith("/project-cost-report") ||
      pathname.startsWith("/finance/reports/"),

    salesPurchase:
      pathname.startsWith("/finance-entry") ||
      pathname.startsWith("/finance/reports/gst/"),
    financeReports: pathname.startsWith("/finance/reports/"),
    gstReports: pathname.startsWith("/finance/reports/gst/"),
    tdsReports: pathname.startsWith("/finance/reports/tds/"),
    expense: pathname.startsWith("/company-expenses"),
    sales:
      pathname.startsWith("/sales") ||
      pathname.startsWith("/quotation") ||
      pathname.startsWith("/proforma-invoice") ||
      pathname.startsWith("/invoice"),

    masters:
      pathname.startsWith("/sales/services") ||
      pathname.startsWith("/sales/clients") ||
      pathname.startsWith("/sales/banks") ||
      pathname.startsWith("/sales/terms"),

    purchase: pathname.startsWith("/purchase"),
    purchaseMasters: pathname.startsWith("/purchase/vendors"),

    internalManagement:
      pathname.startsWith("/software") || pathname.startsWith("/hardware"),

    assets:
      pathname.startsWith("/software") || pathname.startsWith("/hardware"),
  });

  const [openGroup, setOpenGroup] = useState({
    tools: false,
    messenger: false,
    email: false,
    hr: false,
    hiring: false,
    operations: false,
    architect: false,
    projectManagement: false,
    finance: false,
    sales: false,
    purchase: false,
    purchaseMasters: false,
    masters: false,
    salesPurchase: false,
    financeReports: false,
    gstReports: false,
    tdsReports: false,

    expense: false,
    internalManagement: false,
    assets: false,
  });

  useEffect(() => {
    const routeState = routeOpenState(location.pathname);

    setOpenGroup((previous) => {
      const next = { ...previous };

      Object.keys(routeState).forEach((key) => {
        if (routeState[key]) {
          next[key] = true;
        }
      });

      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setOpenGroup((previous) => {
      const nextValue = !previous[key];

      // Nested groups: parent group ko close kiye bina open/close honge.
      if (
        key === "salesPurchase" ||
        key === "financeReports" ||
        key === "gstReports" ||
        key === "tdsReports" ||
        key === "expense" ||
        key === "sales" ||
        key === "masters" ||
        key === "purchase" ||
        key === "purchaseMasters" ||
        key === "assets" ||
        key === "architect"
      ) {
        return {
          ...previous,
          [key]: nextValue,
        };
      }

      // Main sidebar groups me ek time par ek main group open rahega.
      return {
        tools: key === "tools" ? nextValue : false,
        messenger: key === "messenger" ? nextValue : false,
        email: key === "email" ? nextValue : false,
        hr: key === "hr" ? nextValue : false,

        hiring: key === "hiring" ? nextValue : false,

        operations: key === "operations" ? nextValue : false,

        architect:
          key === "operations" && nextValue ? previous.architect : false,

        projectManagement: key === "projectManagement" ? nextValue : false,

        sales: key === "finance" && nextValue ? previous.sales : false,

        masters: key === "finance" && nextValue ? previous.masters : false,
        purchase: key === "finance" && nextValue ? previous.purchase : false,

        purchaseMasters:
          key === "finance" && nextValue ? previous.purchaseMasters : false,
        finance: key === "finance" ? nextValue : false,

        salesPurchase:
          key === "finance" && nextValue ? previous.salesPurchase : false,
        financeReports:
          key === "finance" && nextValue ? previous.financeReports : false,
        gstReports:
          key === "finance" && nextValue ? previous.gstReports : false,
        tdsReports:
          key === "finance" && nextValue ? previous.tdsReports : false,
        expense: key === "finance" && nextValue ? previous.expense : false,

        internalManagement: key === "internalManagement" ? nextValue : false,

        assets:
          key === "internalManagement" && nextValue ? previous.assets : false,
      };
    });
  };

  const handleNavClick = () => {
    if (isMobile) close();
  };

  const getNavClass = ({ isActive }) =>
    `adm-subitem${isActive ? " adm-subitem--active" : ""}`;

  const groupHeaderClass = (isOpen) =>
    `adm-group-header${isOpen ? " adm-group-header--open" : ""}`;

  const subToggleClass = (isOpen) =>
    `adm-sub-toggle${isOpen ? " adm-sub-toggle--open" : ""}`;

  const Menu = (
    <>
      <div
        className="adm-brand"
        onClick={() => {
          navigate("/admin");
          handleNavClick();
        }}
      >
        <div className="adm-logo-dot" />
        <span className="adm-brand-text">Admin</span>
      </div>

      <nav>
        {/* Admin Dashboard */}
        <NavLink
          to="/admin"
          end
          onClick={handleNavClick}
          className={({ isActive }) =>
            `adm-item${isActive ? " adm-item--active" : ""}`
          }
        >
          <ColorIcon icon="🏠" bg="#dbeafe" />
          <span className="adm-item-label">Admin Dashboard</span>
        </NavLink>

        {/* Tool */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("tools")}
            className={groupHeaderClass(openGroup.tools)}
          >
            <ColorIcon icon="🛠️" bg="#fef3c7" />
            <span className="adm-group-title">Tool</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.tools} />
            </span>
          </button>

          {openGroup.tools && (
            <div className="adm-sublist">
              <NavLink
                to="/tools/notes"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📝" bg="#e0f2fe" />
                <span>Notes</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Messenger */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("messenger")}
            className={groupHeaderClass(openGroup.messenger)}
          >
            <ColorIcon icon="💬" bg="#fce7f3" />
            <span className="adm-group-title">
              Messenger
              {unreadCount > 0 && (
                <span className="adm-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.messenger} />
            </span>
          </button>

          {openGroup.messenger && (
            <div className="adm-sublist">
              <NavLink
                to="/messenger"
                end
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="👤" bg="#e0f2fe" />
                User Message
              </NavLink>

              <NavLink
                to="/messenger/project-communication"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📁" bg="#fef3c7" />
                Project-wise
              </NavLink>
           
            </div>
          )}
        </div>

        {/* ================= EMAIL ================= */}
        <NavLink
          to="/email/inbox"
          onClick={handleNavClick}
          className={`adm-item${
            location.pathname.startsWith("/email") ? " adm-item--active" : ""
          }`}
        >
          <ColorIcon icon="📧" bg="#dbeafe" />
          <span className="adm-item-label">Email</span>
        </NavLink>
        {/* ================= END EMAIL ================= */}

        {/* HR */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("hr")}
            className={groupHeaderClass(openGroup.hr)}
          >
            <ColorIcon icon="👥" bg="#ede9fe" />
            <span className="adm-group-title">HR</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.hr} />
            </span>
          </button>

          {openGroup.hr && (
            <div className="adm-sublist">
              <NavLink
                to="/hr/create-mail-account"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📧" bg="#dbeafe" />
                Create Mail Account
              </NavLink>

              <NavLink
                to="/admin/log-summary"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📄" bg="#e0f2fe" />
                Logs
              </NavLink>

              <NavLink
                to="/employee-drawing-report"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📊" bg="#dcfce7" />

                <span className="adm-employee-text">
                  <span>Employee Working</span>
                  <span>Report</span>
                </span>
              </NavLink>

            

              <NavLink
                to="/leave-management"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Leave Management
              </NavLink>

              <NavLink
                to="/manage-employees"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="👥" bg="#ede9fe" />
                Manage Employee
              </NavLink>

              <NavLink
                to="/role-permissions"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🔐" bg="#fef3c7" />
                Role &amp; Permission
              </NavLink>

              <NavLink
                to="/admin-employee-review-detail"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="⭐" bg="#fef9c3" />
                Create Employee Review
              </NavLink>

              
            </div>
          )}
        </div>

        {/* Hiring Management */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("hiring")}
            className={groupHeaderClass(openGroup.hiring)}
          >
            <ColorIcon icon="🧑‍💼" bg="#dcfce7" />
            <span className="adm-group-title">Hiring Management</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.hiring} />
            </span>
          </button>

          {openGroup.hiring && (
            <div className="adm-sublist">
              <NavLink
                to="/hr-dashboard"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📊" bg="#dbeafe" />
                Hiring Dashboard
              </NavLink>

              <NavLink
                to="/hr/job-openings"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="💼" bg="#fef3c7" />
                Job Openings
              </NavLink>

              <NavLink
                to="/hr/candidates"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="👤" bg="#ede9fe" />
                Candidates
              </NavLink>

              <NavLink
                to="/hr/interviews"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🗓️" bg="#e0f2fe" />
                Interviews
              </NavLink>

              <NavLink
                to="/hr/offers"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📄" bg="#fce7f3" />
                Offers
              </NavLink>

              <NavLink
                to="/hr/joining"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🤝" bg="#dcfce7" />
                Upcoming Joinings
              </NavLink>

              <NavLink
                to="/hr/closed-hiring"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Closed Hiring
              </NavLink>

              <NavLink
                to="/hr/recruitment-reports"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📈" bg="#dbeafe" />
                Recruitment Reports
              </NavLink>
            </div>
          )}
        </div>

        {/* Operations */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("operations")}
            className={groupHeaderClass(openGroup.operations)}
          >
            <ColorIcon icon="⚙️" bg="#fef3c7" />
            <span className="adm-group-title">Operations</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.operations} />
            </span>
          </button>

          {openGroup.operations && (
            <div className="adm-sublist">
              <NavLink
                to="/transmittal"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📤" bg="#e0f2fe" />
                Transmittal
              </NavLink>

              <NavLink
                to="/checklist"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Checklist
              </NavLink>

              <NavLink
                to="/projects/new"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🏗️" bg="#fef3c7" />
                Add Project
              </NavLink>

              <NavLink
                to="/drawing-master"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📐" bg="#ede9fe" />
                Drawing Master
              </NavLink>

              <button
                type="button"
                onClick={() => toggleGroup("architect")}
                className={subToggleClass(openGroup.architect)}
              >
                <span className="adm-sub-title">
                  <ColorIcon icon="🏛️" bg="#e0f2fe" />
                  Architect
                </span>

                <Chevron open={openGroup.architect} size={14} />
              </button>

              {openGroup.architect && (
                <div className="adm-nested">
                  <NavLink
                    to="/construction-report"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🏗️" bg="#dcfce7" />
                    Construction Report
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Project Management — its own top-level heading (not nested under Operations) */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("projectManagement")}
            className={groupHeaderClass(openGroup.projectManagement)}
          >
            <ColorIcon icon="📁" bg="#fef3c7" />
            <span className="adm-group-title">Project Management</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.projectManagement} />
            </span>
          </button>

          {openGroup.projectManagement && (
            <div className="adm-sublist">
              <NavLink
                to="/project-management-dashboard"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📊" bg="#dbeafe" />
                Dashboard
              </NavLink>

              <NavLink
                to="/task-board"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🗂️" bg="#fce7f3" />
                Task Board
              </NavLink>

              <NavLink
                to="/gantt-chart"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📈" bg="#dbeafe" />
                Gantt Chart
              </NavLink>

              <NavLink
                to="/calendar"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📅" bg="#dcfce7" />
                Calendar
              </NavLink>

              <NavLink
                to="/drawings"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📐" bg="#ede9fe" />
                Drawings
              </NavLink>

              <NavLink
                to="/boq-estimates"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="💰" bg="#fef9c3" />
                BOQ & Estimates
              </NavLink>

              <NavLink
                to="/site-management"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🏗️" bg="#dcfce7" />
                Site Management
              </NavLink>

              <NavLink
                to="/procurement"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="🛒" bg="#fce7f3" />
                Procurement
              </NavLink>

              <NavLink
                to="/client-approval"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="✅" bg="#dcfce7" />
                Client Approval
              </NavLink>

              <NavLink
                to="/reports"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📑" bg="#dbeafe" />
                Reports
              </NavLink>
            </div>
          )}
        </div>

        {/* Finance */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("finance")}
            className={groupHeaderClass(openGroup.finance)}
          >
            <ColorIcon icon="💰" bg="#dcfce7" />
            <span className="adm-group-title">Finance</span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.finance} />
            </span>
          </button>

          {openGroup.finance && (
            <div className="adm-sublist">
              <NavLink
                to="/finance"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="📈" bg="#dcfce7" />
                Finance Dashboard
              </NavLink>
              <NavLink
                to="/project-cost-report"
                onClick={handleNavClick}
                className={getNavClass}
              >
                <SubIcon icon="💵" bg="#dcfce7" />
                Project Cost
              </NavLink>

              {/* Book Keeping > Finance Entry / Report > GST / TDS */}
              <button
                type="button"
                onClick={() => toggleGroup("salesPurchase")}
                className={subToggleClass(openGroup.salesPurchase)}
              >
                <span className="adm-sub-title">
                  <ColorIcon icon="📂" bg="#dbeafe" /> Book Keeping
                </span>
                <Chevron open={openGroup.salesPurchase} size={14} />
              </button>
              {openGroup.salesPurchase && (
                <div className="adm-nested">
                  <NavLink
                    to="/finance-entry"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="💳" bg="#dbeafe" /> Finance Entry
                  </NavLink>
                  <button
                    type="button"
                    onClick={() => toggleGroup("financeReports")}
                    className={subToggleClass(openGroup.financeReports)}
                  >
                    <span className="adm-sub-title">
                      <SubIcon icon="📊" bg="#dcfce7" /> Report
                    </span>
                    <Chevron open={openGroup.financeReports} size={14} />
                  </button>
                  {openGroup.financeReports && (
                    <div className="adm-master-nested">
                      <button
                        type="button"
                        onClick={() => toggleGroup("gstReports")}
                        className={subToggleClass(openGroup.gstReports)}
                      >
                        <span className="adm-sub-title">
                          <SubIcon icon="🧾" bg="#fef3c7" /> GST
                        </span>
                        <Chevron open={openGroup.gstReports} size={14} />
                      </button>
                      {openGroup.gstReports && (
                        <div className="adm-master-nested">
                          <NavLink
                            to="/finance/reports/gst/r1-sales"
                            onClick={handleNavClick}
                            className={getNavClass}
                          >
                            <SubIcon icon="📄" bg="#dcfce7" /> R1 Sales
                          </NavLink>
                          <NavLink
                            to="/finance/reports/gst/3b-purchase"
                            onClick={handleNavClick}
                            className={getNavClass}
                          >
                            <SubIcon icon="📄" bg="#dbeafe" /> 3B Purchase
                          </NavLink>
                        </div>
                      )}

                      {/* ================= TDS REPORT FOLDER ================= */}
                      <button
                        type="button"
                        onClick={() => toggleGroup("tdsReports")}
                        className={subToggleClass(openGroup.tdsReports)}
                      >
                        <span className="adm-sub-title">
                          <SubIcon icon="📁" bg="#e0f2fe" />
                          TDS
                        </span>

                        <Chevron open={openGroup.tdsReports} size={14} />
                      </button>

                      {openGroup.tdsReports && (
                        <div className="adm-master-nested">
                          <NavLink
                            to="/finance/reports/tds/receivable"
                            onClick={handleNavClick}
                            className={getNavClass}
                          >
                            <SubIcon icon="📥" bg="#dcfce7" />
                            TDS Receivable
                          </NavLink>

                          <NavLink
                            to="/finance/reports/tds/payable"
                            onClick={handleNavClick}
                            className={getNavClass}
                          >
                            <SubIcon icon="💳" bg="#fef3c7" />
                            TDS Payment
                          </NavLink>
                        </div>
                      )}
                      {/* ================= END TDS FOLDER ================= */}
                    </div>
                  )}
                </div>
              )}

              {/* ================= SALES ================= */}
              <button
                type="button"
                onClick={() => toggleGroup("sales")}
                className={subToggleClass(openGroup.sales)}
              >
                <span className="adm-sub-title">
                  <ColorIcon icon="💼" bg="#e0f2fe" />
                  Sales
                </span>

                <Chevron open={openGroup.sales} size={14} />
              </button>

              {openGroup.sales && (
                <div className="adm-nested">
                  <NavLink
                    to="/sales/dashboard"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📊" bg="#dbeafe" />
                    Sales Dashboard
                  </NavLink>

                  <button
                    type="button"
                    onClick={() => toggleGroup("masters")}
                    className={subToggleClass(openGroup.masters)}
                  >
                    <span className="adm-sub-title">
                      <SubIcon icon="🗂️" bg="#ede9fe" />
                      Masters
                    </span>

                    <Chevron open={openGroup.masters} size={14} />
                  </button>

                  {openGroup.masters && (
                    <div className="adm-master-nested">
                      <NavLink
                        to="/sales/services"
                        onClick={handleNavClick}
                        className={getNavClass}
                      >
                        <SubIcon icon="🛠️" bg="#dcfce7" />
                        Service Master
                      </NavLink>

                      <NavLink
                        to="/sales/clients"
                        onClick={handleNavClick}
                        className={getNavClass}
                      >
                        <SubIcon icon="👥" bg="#ede9fe" />
                        Client Master
                      </NavLink>

                      <NavLink
                        to="/sales/banks"
                        onClick={handleNavClick}
                        className={getNavClass}
                      >
                        <SubIcon icon="🏦" bg="#fef3c7" />
                        Bank Detail Master
                      </NavLink>

                      <NavLink
                        to="/sales/terms"
                        onClick={handleNavClick}
                        className={getNavClass}
                      >
                        <SubIcon icon="📋" bg="#fce7f3" />
                        Terms & Conditions
                      </NavLink>
                    </div>
                  )}

                  <NavLink
                    to="/sales/quotations"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📝" bg="#e0f2fe" />
                    Quotations
                  </NavLink>

                  <NavLink
                    to="/sales/proforma-invoices"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📄" bg="#fef9c3" />
                    Proforma Invoices
                  </NavLink>

                  <NavLink
                    to="/sales/invoices"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🧾" bg="#dcfce7" />
                    Invoices
                  </NavLink>
                </div>
              )}

              {/* ================= PURCHASE ================= */}
              <button
                type="button"
                onClick={() => toggleGroup("purchase")}
                className={subToggleClass(openGroup.purchase)}
              >
                <span className="adm-sub-title">
                  <ColorIcon icon="🛒" bg="#fef3c7" />
                  Purchase
                </span>

                <Chevron open={openGroup.purchase} size={14} />
              </button>

              {openGroup.purchase && (
                <div className="adm-nested">
                  <NavLink
                    to="/purchase/dashboard"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📊" bg="#dbeafe" />
                    Purchase Dashboard
                  </NavLink>

                  <NavLink
                    to="/purchase/quotations"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📝" bg="#e0f2fe" />
                    Quotations
                  </NavLink>

                  <NavLink
                    to="/purchase/proforma-invoices"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="📄" bg="#fef9c3" />
                    Proforma Invoices
                  </NavLink>

                  <NavLink
                    to="/purchase/invoices"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🧾" bg="#dcfce7" />
                    Invoices
                  </NavLink>

                  {/* ================= PURCHASE MASTERS ================= */}
                  <button
                    type="button"
                    onClick={() => toggleGroup("purchaseMasters")}
                    className={subToggleClass(openGroup.purchaseMasters)}
                  >
                    <span className="adm-sub-title">
                      <SubIcon icon="🗂️" bg="#ede9fe" />
                      Masters
                    </span>

                    <Chevron open={openGroup.purchaseMasters} size={14} />
                  </button>

                  {openGroup.purchaseMasters && (
                    <div className="adm-master-nested">
                      <NavLink
                        to="/purchase/vendors"
                        onClick={handleNavClick}
                        className={getNavClass}
                      >
                        <SubIcon icon="🏢" bg="#ede9fe" />
                        Vendor Master
                      </NavLink>
                    </div>
                  )}
                </div>
              )}
              {/* ================= END PURCHASE ================= */}

              {openGroup.expense && (
                <div className="adm-nested">
                  <NavLink
                    to="/company-expenses/svb"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🏢" bg="#ede9fe" />
                    SVB
                  </NavLink>

                  <NavLink
                    to="/company-expenses/sdc"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🏢" bg="#dbeafe" />
                    SDC
                  </NavLink>

                  <NavLink
                    to="/company-expenses/magnira"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🏢" bg="#fef3c7" />
                    Magnira
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admin & Internal Management */}
        <div className="adm-group">
          <button
            type="button"
            onClick={() => toggleGroup("internalManagement")}
            className={groupHeaderClass(openGroup.internalManagement)}
          >
            <ColorIcon icon="🛡️" bg="#e0f2fe" />
            <span className="adm-group-title">
              Admin &amp; Internal Management
            </span>
            <span className="adm-chevron-wrap">
              <Chevron open={openGroup.internalManagement} />
            </span>
          </button>

          {openGroup.internalManagement && (
            <div className="adm-sublist">
              <button
                type="button"
                onClick={() => toggleGroup("assets")}
                className={subToggleClass(openGroup.assets)}
              >
                <span className="adm-sub-title">
                  <ColorIcon icon="💻" bg="#e0f2fe" />
                  Assets
                </span>
                <Chevron open={openGroup.assets} size={14} />
              </button>

              {openGroup.assets && (
                <div className="adm-nested">
                  <NavLink
                    to="/software"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="💻" bg="#e0f2fe" />
                    Software Master
                  </NavLink>

                  <NavLink
                    to="/hardware"
                    onClick={handleNavClick}
                    className={getNavClass}
                  >
                    <SubIcon icon="🖥️" bg="#ede9fe" />
                    Hardware Master
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );

  const headerHasHamburger =
    typeof window !== "undefined" && window.__HEADER_HAS_HAMBURGER__;

  const showFab = !hideFab && isMobile && !headerHasHamburger;

  return (
    <>
      {!isMobile && <aside className="adm-sidebar">{Menu}</aside>}

      {isMobile &&
        createPortal(
          <>
            <div
              onClick={close}
              className={`adm-backdrop${drawerOpen ? " adm-backdrop--show" : ""}`}
            />

            <aside
              className={`adm-sidebar adm-sidebar--drawer${
                drawerOpen ? " adm-sidebar--open" : ""
              }`}
              onMouseDownCapture={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              {Menu}
            </aside>

            {showFab && (
              <button
                type="button"
                aria-label="Open menu"
                onClick={open}
                className="adm-fab"
              >
                <Icon name="menu" size={22} />
              </button>
            )}
          </>,
          document.body,
        )}
    </>
  );
};

export default AdminSidebar;